import React, { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../utils/apiBase';
import type { LeagueCardData } from './league-card';
import {
  type DisciplineFilter,
  buildFeed,
  filterEntries,
  groupByMonth,
} from '../events/event-entries';
import { EventRow, MonthSection } from '../events/event-row';

const DISCIPLINE_CHIPS: { value: DisciplineFilter; label: string }[] = [
  { value: 'ALL', label: 'Każda' },
  { value: 'singles', label: 'Singiel' },
  { value: 'doubles', label: 'Debel' },
  { value: 'mixed', label: 'Mix' },
];

const LeaguesList: React.FC = () => {
  const [leagues, setLeagues] = useState<LeagueCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [discipline, setDiscipline] = useState<DisciplineFilter>('ALL');

  useEffect(() => {
    fetch(apiUrl('/api/public/leagues'))
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json() as Promise<{ items: LeagueCardData[] }>;
      })
      .then((data) => {
        setLeagues(data.items);
        setLoading(false);
      })
      .catch(() => {
        setError('Nie udało się załadować lig.');
        setLoading(false);
      });
  }, []);

  // Same shelves as /events, so a league hidden by its club there stays hidden here too.
  const feed = useMemo(() => buildFeed([], leagues, new Date()), [leagues]);
  const filters = useMemo(
    () => ({ search, discipline, kind: 'league' as const }),
    [search, discipline]
  );
  const upcoming = useMemo(() => filterEntries(feed.upcoming, filters), [feed, filters]);
  const ongoing = useMemo(() => filterEntries(feed.ongoing, filters), [feed, filters]);

  if (loading) {
    return <div className="events-loading">Ładowanie...</div>;
  }

  if (error) {
    return <div className="events-error">{error}</div>;
  }

  function resetFilters() {
    setSearch('');
    setDiscipline('ALL');
  }

  const filtersActive = Boolean(search.trim()) || discipline !== 'ALL';
  const visibleCount = upcoming.length + ongoing.length;
  const totalCount = feed.upcoming.length + feed.ongoing.length;
  const openSignups = [...upcoming, ...ongoing].filter((entry) => entry.signupOpen).length;

  if (totalCount === 0) {
    return (
      <div className="events-list">
        <div className="events-list-header">
          <h1>Ligi</h1>
        </div>
        <p className="events-empty">Nie ma teraz aktywnych lig. Zajrzyj za chwilę.</p>
      </div>
    );
  }

  return (
    <div className="events-list">
      <div className="events-list-header">
        <h1>Ligi</h1>
      </div>

      <div className="leagues-filter">
        <input
          type="search"
          className="leagues-filter__search"
          placeholder="Szukaj ligi, klubu lub miejsca…"
          aria-label="Szukaj ligi"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="leagues-filter__chips" role="radiogroup" aria-label="Dyscyplina">
          {DISCIPLINE_CHIPS.map((chip) => (
            <button
              key={chip.value}
              role="radio"
              aria-checked={discipline === chip.value}
              className={`leagues-filter__chip${
                discipline === chip.value ? ' leagues-filter__chip--active' : ''
              }`}
              onClick={() => setDiscipline(chip.value)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      <p className="event-feed__summary">
        <span className="event-feed__summary-count">
          {filtersActive ? `${visibleCount} z ${totalCount}` : totalCount}
        </span>
        {filtersActive ? ' pasujących lig' : ' aktywnych lig'}
        {openSignups > 0 && (
          <span className="event-feed__summary-signup">
            <span className="signup-pulse-dot" aria-hidden="true" />
            {openSignups} z otwartymi zapisami
          </span>
        )}
        {filtersActive && (
          <button type="button" className="event-feed__summary-reset" onClick={resetFilters}>
            Wyczyść filtry
          </button>
        )}
      </p>

      {visibleCount === 0 ? (
        <div className="leagues-empty">
          <p>Nic nie pasuje do filtrów.</p>
          <button className="leagues-empty__reset" onClick={resetFilters}>
            Pokaż wszystkie
          </button>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="event-feed">
              <h2 className="event-feed__title">
                Najbliższe kolejki{' '}
                <span className="event-feed__title-count">{upcoming.length}</span>
              </h2>
              {groupByMonth(upcoming).map((group) => (
                <MonthSection key={group.key} group={group} />
              ))}
            </div>
          )}

          {ongoing.length > 0 && (
            <div className="event-feed">
              <h2 className="event-feed__title">
                Bez zaplanowanego terminu{' '}
                <span className="event-feed__title-count">{ongoing.length}</span>
              </h2>
              <p className="event-feed__hint">
                Ligi w toku — organizator nie ogłosił jeszcze kolejnego terminu.
              </p>
              <ul className="event-rows event-rows--muted">
                {ongoing.map((entry) => (
                  <EventRow
                    key={entry.key}
                    entry={entry}
                    dateCaption={entry.startDate ? 'ostatnia kolejka' : undefined}
                  />
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LeaguesList;
